import { useQuery } from '@tanstack/react-query'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { type LocaleSettingsDTO, localeSettingsService } from '@/services/locale-settings.service'

const UNCONFIGURED: LocaleSettingsDTO = {
	enabledLocales: [],
	requiredLocales: [],
	defaultLocale: null,
	updatedAt: null,
}

/**
 * The viewer's tenant slug, straight from the `/auth/me` session (TO-628). A loaded
 * session with a tenant but no slug is an error, so slug consumers fail closed
 * exactly as they did when the old per-tenant lookup failed.
 */
export function useTenantSlug(): { slug: string | null; isError: boolean } {
	const { tenantId, tenantSlug, isLoading } = useViewerPermissions()
	return { slug: tenantSlug, isError: !isLoading && tenantId !== null && tenantSlug === null }
}

/**
 * The tenant's locale settings for the product editor (TO-416 REQ-003/REQ-006).
 * A refused read (e.g. no `tenant.read`) counts as an unconfigured tenant.
 */
export function useTenantLocales(): { settings: LocaleSettingsDTO; isLoading: boolean } {
	const { slug, isError: slugError } = useTenantSlug()
	const query = useQuery({
		queryKey: ['locale-settings', slug],
		queryFn: () => localeSettingsService.get(slug as string),
		enabled: slug !== null,
		// Shared by several editor sections; the settings screen writes the cache on save.
		staleTime: 30_000,
	})
	const settled = slugError || query.isError || query.isSuccess
	return { settings: query.data ?? UNCONFIGURED, isLoading: !settled }
}
